import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CATEGORIES, getSubcats } from "@/lib/categories";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Search, Link2, Plus, Upload } from "lucide-react";
import { toast } from "sonner";

export default function Products() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [productsHydrated, setProductsHydrated] = useState(false);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");           // "all" | category value
  const [sub, setSub] = useState("all");           // "all" | subcategory value
  const [scope, setScope] = useState("all");       // "all" | "mine"
  const [clipUrl, setClipUrl] = useState("");
  const [clipCat, setClipCat] = useState("interior");
  const [clipSub, setClipSub] = useState("");
  const [clipping, setClipping] = useState(false);
  const lastLoadedAt = useRef(0);

  const subOptions = cat !== "all" ? getSubcats(cat) : [];

  // Reset subcategory when category changes
  useEffect(() => { setSub("all"); }, [cat]);

  const load = async (retry = 0) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (cat !== "all") params.set("category", cat);
    if (sub !== "all") params.set("subcategory", sub);
    if (scope === "mine" && user) params.set("mine", "true");
    params.set("limit", "200");
    try {
      const { data } = await api.get(`/products?${params.toString()}`);
      setProducts(data || []);
      setProductsHydrated(true);
      lastLoadedAt.current = Date.now();
    } catch (e) {
      if (retry < 2) setTimeout(() => load(retry + 1), 600 * (retry + 1));
      else setProductsHydrated(true);
    }
  };

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-line */ }, [q, cat, sub, scope, user]);

  // Auto-refresh "My Library" when the user returns to the tab — fixes the
  // perceived "lag" between clipping in another tab/the extension and seeing
  // the item appear here. Throttled to 1×/3s to avoid hammering the API.
  useEffect(() => {
    if (scope !== "mine") return;
    const onFocus = () => {
      if (Date.now() - lastLoadedAt.current > 3000) load();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [scope]);

  const clip = async () => {
    if (!user) { toast.error("Login to clip products"); return nav("/login"); }
    if (!clipUrl) return;
    setClipping(true);
    try {
      const { data } = await api.post("/products/clip", {
        url: clipUrl,
        category: clipCat,
        subcategory: clipSub,
      });
      toast.success(`Clipped: ${data.title}`);
      setClipUrl("");
      setScope("mine");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not extract product from that URL";
      toast.error(msg);
    } finally { setClipping(false); }
  };

  const onUpload = async (filesList) => {
    if (!user) { toast.error("Login to upload"); return nav("/login"); }
    const files = Array.from(filesList || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("category", cat === "all" ? "interior" : cat);
    if (sub !== "all") fd.append("subcategory", sub);
    try {
      const { data } = await api.post("/products/upload-batch", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const created = data.created?.length || 0;
      const failed = data.errors?.length || 0;
      if (created) toast.success(`${created} item${created === 1 ? "" : "s"} uploaded`);
      if (failed) toast.error(`${failed} file(s) failed`);
      setScope("mine");
      load();
    } catch {
      toast.error("Upload failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="border-b border-[#E5E5E5] pb-8 mb-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#9CA47E] mb-3">⌗ The Archive</div>
            <h1 className="font-heading text-5xl sm:text-6xl tracking-tight">Gathered Pieces</h1>
            <p className="font-body text-[#595959] mt-3 max-w-xl">Every piece gathered by the collective. Compose them into your spaces.</p>
          </div>
          <Button data-testid="products-create-set" onClick={() => nav("/editor")} className="rounded-none bg-[#9CA47E] hover:bg-[#7E876A] text-white px-6 py-6">
            <Plus className="w-4 h-4 mr-2" /> Create a Set
          </Button>
        </div>

        {/* Browser extension promo */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center bg-[#333333] text-white p-5 sm:p-6 border border-[#333333]">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#9CA47E] mb-2">◇ New · v0.2 · MV3</div>
            <h3 className="font-heading text-2xl sm:text-3xl tracking-tight">VoraStyle Clipper · browser extension</h3>
            <p className="font-body text-sm text-white/70 mt-2 max-w-2xl leading-relaxed">
              Right-click any image on any retailer, choose <em>Clip image to VoraStyle</em>, pick a chapter, and the piece lands in your archive — ready to drag onto a canvas.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <a data-testid="clipper-install" href={`${process.env.REACT_APP_BACKEND_URL}/api/clipper/download`} download="vorastyle-clipper.zip" className="px-4 py-2.5 bg-[#9CA47E] hover:bg-[#7E876A] text-white font-mono text-[11px] uppercase tracking-[0.18em] inline-flex items-center justify-center gap-1.5">
              ⬇ Download ZIP
            </a>
            <Link data-testid="clipper-docs" to="/clipper" className="px-4 py-2.5 bg-transparent border border-white text-white hover:bg-white hover:text-[#333333] font-mono text-[11px] uppercase tracking-[0.18em] inline-flex items-center justify-center transition-colors">
              How to install
            </Link>
          </div>
        </div>

        {/* Top-level tabbed nav (Fashion · Home · Design Elements) */}
        <div className="mt-8 flex border-b border-[#EFEFEF] bg-white" data-testid="products-tabs">
          <button
            type="button"
            data-testid="products-tab-all"
            onClick={() => { setCat("all"); setSub("all"); }}
            className={`flex-1 py-3 font-mono text-[11px] uppercase tracking-[0.25em] cursor-pointer transition-colors ${cat === "all" ? "bg-[#333333] text-white" : "text-[#595959] hover:text-[#333333]"}`}
          >
            All
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c.value}
              type="button"
              data-testid={`products-tab-${c.value}`}
              onClick={() => { setCat(c.value); setSub("all"); }}
              className={`flex-1 py-3 font-mono text-[11px] uppercase tracking-[0.25em] cursor-pointer transition-colors border-l border-[#EFEFEF] ${cat === c.value ? "bg-[#333333] text-white" : "text-[#595959] hover:text-[#333333]"}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Filter / search bar */}
        <div className="mt-6 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="flex gap-1.5 flex-wrap items-center">
            {user && (
              <div className="flex border border-[#E5E5E5] mr-2" data-testid="products-scope-toggle">
                <button
                  type="button"
                  data-testid="products-scope-all"
                  onClick={() => setScope("all")}
                  className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest border-r border-[#E5E5E5] cursor-pointer transition-colors ${scope === "all" ? "bg-[#333333] text-white" : "bg-white text-[#333333] hover:bg-[#F2EDE3]"}`}
                >
                  Everyone
                </button>
                <button
                  type="button"
                  data-testid="products-scope-mine"
                  onClick={() => setScope("mine")}
                  className={`px-3 py-2 font-mono text-[10px] uppercase tracking-widest cursor-pointer transition-colors ${scope === "mine" ? "bg-[#9CA47E] text-white" : "bg-white text-[#333333] hover:bg-[#F2EDE3]"}`}
                >
                  My Archive
                </button>
              </div>
            )}
            {/* Sub-category chip row — only shown when a category tab is active */}
            {subOptions.length > 0 && (
              <div className="flex gap-1.5 overflow-x-auto max-w-[680px] pb-1" data-testid="products-subcat-row">
                <button
                  type="button"
                  data-testid="products-subcat-all"
                  onClick={() => setSub("all")}
                  className={`shrink-0 px-3 py-2 font-mono text-[10px] uppercase tracking-widest border cursor-pointer transition-colors ${sub === "all" ? "bg-[#333333] text-white border-[#333333]" : "bg-white border-[#E5E5E5] text-[#595959] hover:border-[#333333] hover:text-[#333333]"}`}
                >
                  All
                </button>
                {subOptions.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    data-testid={`products-subcat-${s.value}`}
                    onClick={() => setSub(s.value)}
                    className={`shrink-0 px-3 py-2 font-mono text-[10px] uppercase tracking-widest border cursor-pointer transition-colors ${sub === s.value ? "bg-[#9CA47E] text-white border-[#9CA47E]" : "bg-white border-[#E5E5E5] text-[#595959] hover:border-[#9CA47E] hover:text-[#9CA47E]"}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="flex gap-3 flex-wrap items-center">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#595959]" />
              <Input
                data-testid="products-search"
                placeholder="Search products"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="rounded-none border-[#E5E5E5] focus-visible:ring-0 focus-visible:border-[#333333] pl-10 py-5"
              />
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button data-testid="products-clip-btn" variant="outline" className="rounded-none border-[#333333] text-xs font-mono uppercase tracking-widest py-5 px-4">
                  <Link2 className="w-3.5 h-3.5 mr-2" /> Clip URL
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none border-[#E5E5E5]">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">Clip from any URL</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <Input
                    data-testid="products-clip-input"
                    placeholder="https://shop.example.com/product/..."
                    value={clipUrl}
                    onChange={(e) => setClipUrl(e.target.value)}
                    className="rounded-none border-[#E5E5E5] focus-visible:ring-0"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      data-testid="products-clip-category"
                      value={clipCat}
                      onChange={(e) => { setClipCat(e.target.value); setClipSub(""); }}
                      className="border border-[#E5E5E5] bg-white px-3 py-2 font-mono text-[11px] uppercase tracking-widest"
                    >
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <select
                      data-testid="products-clip-subcategory"
                      value={clipSub}
                      onChange={(e) => setClipSub(e.target.value)}
                      className="border border-[#E5E5E5] bg-white px-3 py-2 font-mono text-[11px] uppercase tracking-widest"
                    >
                      <option value="">— Chapter —</option>
                      {getSubcats(clipCat).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                  <Button data-testid="products-clip-submit" onClick={clip} disabled={clipping} className="rounded-none w-full bg-[#333333]">
                    {clipping ? "Clipping…" : "Clip product"}
                  </Button>
                  <p className="text-xs font-mono text-[#595959]">We extract the product image, title, brand and price from the page. JS-only retailers (Talbots, Zara, Nordstrom…) require the browser extension instead.</p>
                </div>
              </DialogContent>
            </Dialog>
            <label className="cursor-pointer">
              <input data-testid="products-upload" type="file" accept="image/*" multiple className="hidden" onChange={(e) => onUpload(e.target.files)} />
              <span className="inline-flex items-center px-4 py-2.5 border border-[#333333] text-xs font-mono uppercase tracking-widest hover:bg-[#F2EDE3]">
                <Upload className="w-3.5 h-3.5 mr-2" /> Collect
              </span>
            </label>
          </div>
        </div>
      </div>

      {!productsHydrated ? (
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#595959] py-20 text-center" data-testid="products-loading">
          Gathering pieces…
        </div>
      ) : products.length === 0 ? (
        <div className="font-body text-[#595959] py-20 text-center" data-testid="products-empty">
          {scope === "mine"
            ? "Your archive is empty. Use the Clip URL button or the browser extension to gather pieces."
            : "No gathered pieces found."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {products.map((p) => (
            <div key={p.id} data-testid={`product-tile-${p.id}`} className="group border border-[#E5E5E5] bg-white">
              <div className="aspect-square bg-[#F2EDE3] overflow-hidden">
                <img src={resolveImageUrl(p.image_url)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-3">
                <div className="text-sm font-body truncate">{p.title}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#595959] truncate mt-1">
                  {p.subcategory || p.category}{p.brand && ` · ${p.brand}`}{p.price && ` · ${p.price}`}
                </div>
                <Button data-testid={`product-use-${p.id}`} onClick={() => nav("/editor")} className="rounded-none w-full mt-3 bg-[#333333] hover:bg-[#333] text-white text-[10px] font-mono uppercase tracking-widest py-3">
                  <Plus className="w-3 h-3 mr-1.5" /> Use in a Set
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
